import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { emitCloudSync } from '@/lib/cloudSyncEvents';

export interface ImportTimestamp {
  importedAt: string | null;
  updatedAt: string | null;
}

export function useImportPersistence<T>(
  tableName: 'ibkr_imports' | 'sma_surveillance_imports' | 'revolut_imports'
) {
  const [lastImport, setLastImport] = useState<ImportTimestamp>({ importedAt: null, updatedAt: null });
  const [savedData, setSavedData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const loadSavedImport = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setLoading(false);
        return null;
      }

      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .eq('user_id', session.user.id)
        .order('imported_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error(`Error loading ${tableName}:`, error);
        setLoading(false);
        return null;
      }

      if (data) {
        setLastImport({
          importedAt: data.imported_at,
          updatedAt: data.updated_at
        });
        setSavedData(data as T);
        setLoading(false);
        return data as T;
      }
      
      setLoading(false);
      return null;
    } catch (error) {
      console.error(`Error in loadSavedImport:`, error);
      setLoading(false);
      return null;
    }
  }, [tableName]);

  const saveImport = useCallback(async (importData: Partial<T>) => {
    emitCloudSync({ status: 'saving', source: tableName });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        emitCloudSync({ status: 'error', source: tableName, message: 'Not authenticated' });
        toast({
          title: "Not authenticated",
          description: "Please log in to save your import",
          variant: "destructive"
        });
        return null;
      }

      // Insert FIRST to avoid data loss if the insert fails.
      const { data, error } = await supabase
        .from(tableName)
        .insert({
          user_id: session.user.id,
          ...importData
        } as any)
        .select()
        .single();

      if (error) {
        console.error(`Error saving ${tableName}:`, error);
        emitCloudSync({ status: 'error', source: tableName, message: error.message });
        toast({
          title: "Save failed",
          description: error.message,
          variant: "destructive"
        });
        return null;
      }

      // Verify the row is readable (catches any unexpected RLS / replication issues)
      const { data: verifyData, error: verifyError } = await supabase
        .from(tableName)
        .select('id')
        .eq('id', (data as any).id)
        .maybeSingle();

      if (verifyError || !verifyData?.id) {
        const msg = verifyError?.message || 'Could not verify saved row';
        console.error(`Error verifying ${tableName} save:`, verifyError);
        emitCloudSync({ status: 'error', source: tableName, message: msg });
        toast({
          title: 'Save verification failed',
          description: msg,
          variant: 'destructive',
        });
        return null;
      }

      // Cleanup older imports (best-effort) — keep only latest.
      const { error: cleanupError } = await supabase
        .from(tableName)
        .delete()
        .eq('user_id', session.user.id)
        .neq('id', (data as any).id);

      if (cleanupError) {
        // Not fatal for correctness; we just keep extra history.
        console.warn(`Cleanup old ${tableName} rows failed:`, cleanupError);
      }

      setLastImport({
        importedAt: data.imported_at,
        updatedAt: data.updated_at
      });
      setSavedData(data as T);

      emitCloudSync({ status: 'saved', source: tableName });

      toast({
        title: "Data saved",
        description: `Your import has been saved at ${new Date(data.imported_at).toLocaleString()}`
      });

      return data as T;
    } catch (error) {
      console.error(`Error in saveImport:`, error);
      emitCloudSync({
        status: 'error',
        source: tableName,
        message: error instanceof Error ? error.message : 'Unknown error',
      });
      toast({
        title: "Save failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
      return null;
    }
  }, [tableName, toast]);

  const clearImport = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      await supabase
        .from(tableName)
        .delete()
        .eq('user_id', session.user.id);

      setLastImport({ importedAt: null, updatedAt: null });
      setSavedData(null);
    } catch (error) {
      console.error(`Error clearing ${tableName}:`, error);
    }
  }, [tableName]);

  useEffect(() => {
    loadSavedImport();
  }, [loadSavedImport]);

  return {
    lastImport,
    savedData,
    loading,
    loadSavedImport,
    saveImport,
    clearImport
  };
}

export function formatImportTimestamp(timestamp: string | null): string {
  if (!timestamp) return 'Never';
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}
